import React, { Fragment, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import {
	Button,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from 'reactstrap';

import { Avatar, UserAvatar } from '@techchange/filestack';
import { IconButton, tcBuildSessionURL } from '@techchange/components';
import { messages } from '../../../i18n';

import getNotificationActionMap from '../utils/notificationActionMap';

import styles from './NotificationsNavDropdownMenu.module.scss';

const notificationActionMap = getNotificationActionMap();

export const DROPDOWN_MENU_DOM_ID = 'notifications-nav-item-dropdown-menu';

const propTypes = {

	/**
	 * The list of the user's platform notifications.
	 */
	notifications: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The notification id.
		 */
		id: PropTypes.string.isRequired,

	})),

	/**
	 * The total number of unread notifications for the user.
	 */
	unreadCount: PropTypes.number.isRequired,

	/**
	 * True, if there was an error loading the required data for the dropdown menu.
	 */
	showError: PropTypes.bool,

	/**
	 * The state of loading the required data for the dropdown menu.
	 */
	menuLoadState: PropTypes.shape({

		/**
		 * True, if loading data for the dropdown menu.
		 */
		loading: PropTypes.bool,

		/**
		 * True, if done loading data for the dropdown menu.
		 */
		loadedAll: PropTypes.bool,

	}),

	/**
	 * Callback to handle loading required data for the dropdown menu.
	 */
	onLoadMenuData: PropTypes.func.isRequired,

	/**
	 * Callback to mark a single notification as read.
	 */
	onMarkRead: PropTypes.func.isRequired,

	/**
	 * Callback to mark all of a user's notifications as read.
	 */
	onMarkAllRead: PropTypes.func.isRequired,

};

const NotificationsNavDropdownMenu = (props) => {

	const {
		notifications,
		unreadCount,
		showError,
		menuLoadState,
		onMarkRead,
		onMarkAllRead,
		onLoadMenuData,
	} = props;

	const intl = useIntl();

	// Keep track of the dropdown menu state
	const [menuState, setMenuState] = useState({
		isOpen: false,
		fetchedData: false,
	});

	/**
	 * Toggles the menu open/closed. Loads the initial batch of data if needed.
	 */
	const toggle = () => {

		if (!menuState.fetchedData) {
			onLoadMenuData();
		}

		setMenuState({ isOpen: !menuState.isOpen, fetchedData: true });

	};

	// Filter out notifications without supported object/action display information
	const supportedNotifications = notifications && notifications.filter((notification) => {

		const objectInfo = notificationActionMap[notification.object_type];
		const action = objectInfo.actions[notification.action];

		return Boolean(objectInfo && action);

	});

	return (
		<Dropdown
			id="notifications-nav-item"
			className={styles['dropdown-menu-notification']}
			isOpen={menuState.isOpen}
			toggle={toggle}
			// Allows user to tab to "mark all as read" button
			// See https://github.com/reactstrap/reactstrap/issues/1441 for more details
			a11y={false}
			nav
			inNavbar>
			<DropdownToggle
				className="btn tc-icon-button"
				tag="button"
				aria-label={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_TITLE)}
				title={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_TITLE)}
				nav>
				<IconButton
					// https://github.com/reactstrap/reactstrap/issues/2758
					tag={<span />}
					title={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_TITLE)}
					icon="fal fa-bell fa-xl"
					size="45px"
					countColor="danger"
					count={unreadCount}
					hasCount />
			</DropdownToggle>
			<DropdownMenu
				id={DROPDOWN_MENU_DOM_ID}
				data-bs-popper="static"
				end>
				{showError &&
					<div className={styles['dropdown-menu-item-errors']}>
						<FormattedMessage
							{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_ERROR_MSG} />
					</div>
				}
				{!showError &&
					<Fragment>
						{/* Initial load loading spinner */}
						{!notifications &&
							<h2 className="text-center"><i className="fas fa-circle-notch fa-spin" /></h2>
						}
						{/* Empty state message */}
						{supportedNotifications && supportedNotifications.length === 0 &&
							<div className={styles['dropdown-menu-item-errors']}>
								<FormattedMessage
									{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_EMPTY_MSG} />
							</div>
						}
						{/* Notifications list */}
						{supportedNotifications && supportedNotifications.length > 0 &&
							<Fragment>
								<div className="tc-universal-dropdown-menu-header d-flex flex-wrap">
									<div className="tc-universal-dropdown-menu-header-title me-auto">
										<FormattedMessage
											{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_TITLE} />
									</div>
									<Button
										className={styles['mark-all-read-btn']}
										color="link"
										onClick={onMarkAllRead}
										// Needed to prevent default reactstrap handling of keydown
										// events within dropdown, otherwise user cannot active click with keyboard
										onKeyDown={(event) => {
											event.stopPropagation();
											if (event.key === 'Escape') {
												toggle();
											}
										}}>
										<FormattedMessage
											{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_BUTTON_LABEL_MARK_ALL_READ} />
									</Button>
								</div>
								<DropdownItem divider />
								{supportedNotifications.map((notification) => {

									const objectInfo = notificationActionMap[notification.object_type];
									const action = objectInfo.actions[notification.action];

									return (
										<DropdownItem
											key={notification.id}
											// eslint-disable-next-line max-len
											className={`${styles['dropdown-menu-item']} ${styles['notification']} ${notification.read ? styles['read'] : ''}`}
											tag={notification.session ? 'a' : 'div'}
											href={notification.session
												// eslint-disable-next-line max-len
												? `${tcBuildSessionURL(notification.session)}${objectInfo.getObjectLink(notification)}`
												: undefined
											}
											rel="noopener noreferrer"
											target="_blank"
											title={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATION_DROPDOWN_ITEM_LINK_TITLE)}
											toggle={false}
											onClick={() => onMarkRead(notification.id)}>
											<div className="d-flex w-100">
												{notification.actor &&
													<UserAvatar
														className="me-1"
														user={notification.actor}
														size="45px"
														rounded />
												}
												{!notification.actor &&
													<Avatar
														className="me-1"
														size="45px"
														icon={(<i className="fas fa-bell" />)}
														rounded />
												}
												<div className="notification-description w-100">
													<div className="notification-description-container">
														<span>
															{action.formatMessage(notification, intl, {
																user_name: notification.actor
																	? notification.actor.display_name
																	: null,
																bold: function bold(...chunks) { return (<b>{chunks}</b>); },
															})}
														</span>
														{!notification.read &&
															<div className="unread-marker ms-auto" />
														}
													</div>
													<div className="notification-timestamp">
														{notification.session && notification.session.title}
														{notification.session && '  -  '}
														{moment(notification.created_at).fromNow()}
													</div>
												</div>
											</div>
										</DropdownItem>
									);

								})}
								{/* Lazy load loading spinner */}
								{!menuLoadState.loadedAll && menuLoadState.loading &&
									<div
										className={styles['dropdown-menu-item-loading-indicator']}>
										<i
											className="fas fa-circle-notch fa-spin"
											aria-hidden="true" />
									</div>
								}
							</Fragment>
						}
					</Fragment>
				}
			</DropdownMenu>
		</Dropdown>
	);

};

NotificationsNavDropdownMenu.propTypes = propTypes;

export default NotificationsNavDropdownMenu;
